/**
 * SafetyTimelineCard — unified Trust & Safety feed for a user (user-360).
 *
 * Aggregates reports (in/out), photo-moderation scans, blocks (in/out),
 * internal notes, and admin actions into one reverse-chronological timeline so
 * a moderator can judge a user from a single pane instead of six disconnected
 * queues. Self-contained + best-effort (hides on error).
 *
 * Photo events: every upload logs a scan (`core.photo_moderation_log`), including
 * passes. Only non-pass outcomes are "flagged"; passes render as "Photo processed".
 */
import { useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch } from '@/providers/admin-api';
import { formatDate } from '@/lib/utils';
import {
  Flag,
  Image as ImageIcon,
  Ban,
  StickyNote,
  ShieldAlert,
  ShieldCheck,
  Activity,
} from 'lucide-react';

interface TimelineEvent {
  ts: string;
  kind: string;
  title: string | null;
  detail: string | null;
  actor: string | null;
}

interface TimelineResponse {
  events: TimelineEvent[];
  summary: Record<string, number>;
}

type EventMeta = { label: string; Icon: typeof Flag; tone: 'danger' | 'warn' | 'info' | 'muted' };

const KIND_META: Record<string, EventMeta> = {
  report_received: { label: 'Reported by a user', Icon: Flag, tone: 'danger' },
  report_filed: { label: 'Filed a report', Icon: Flag, tone: 'muted' },
  blocked_by: { label: 'Blocked by a user', Icon: Ban, tone: 'danger' },
  blocked_other: { label: 'Blocked someone', Icon: Ban, tone: 'muted' },
  note: { label: 'Internal note', Icon: StickyNote, tone: 'info' },
  admin_action: { label: 'Admin action', Icon: ShieldAlert, tone: 'info' },
};

/** `detail` is COALESCE(admin_decision, result): pass | fail | rejected | approved. */
export function photoEventMeta(detail: string | null): EventMeta {
  if (detail === 'pass' || detail == null) {
    return { label: 'Photo processed', Icon: ImageIcon, tone: 'muted' };
  }
  return { label: 'Photo flagged', Icon: ImageIcon, tone: 'warn' };
}

const TONE_CLASS: Record<string, string> = {
  danger: 'bg-red-500/15 text-red-500',
  warn: 'bg-yellow-500/15 text-yellow-500',
  info: 'bg-blue-500/15 text-blue-500',
  muted: 'bg-muted text-muted-foreground',
};

const SUMMARY_CHIPS: { key: string; label: string; danger?: boolean }[] = [
  { key: 'reports_received', label: 'reports received', danger: true },
  { key: 'blocked_by', label: 'blocked by', danger: true },
  { key: 'photo_flags', label: 'photo flags', danger: true },
  { key: 'photo_processed', label: 'photos processed' },
  { key: 'reports_filed', label: 'reports filed' },
  { key: 'blocked_other', label: 'blocks made' },
  { key: 'admin_actions', label: 'admin actions' },
  { key: 'notes', label: 'notes' },
];

export function SafetyTimelineCard({ userId }: { userId: string }) {
  const apiUrl = useApiUrl();
  const [data, setData] = useState<TimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminFetch(`${apiUrl}/v1/admin/users/${userId}/safety-timeline`)
      .then((r) => r.json())
      .then((d: TimelineResponse) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, userId]);

  if (failed) return null;

  const events = data?.events ?? [];
  const summary = data?.summary ?? {};
  const hasRisk =
    (summary.reports_received ?? 0) > 0 ||
    (summary.blocked_by ?? 0) > 0 ||
    (summary.photo_flags ?? 0) > 0;

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="mb-4 flex items-center gap-2">
        {hasRisk ? (
          <ShieldAlert className="h-5 w-5 text-red-500" />
        ) : (
          <ShieldCheck className="h-5 w-5 text-green-500" />
        )}
        <h3 className="text-lg font-semibold">Safety timeline</h3>
        <span className="text-sm text-muted-foreground">
          every report, flag, block, note &amp; action in one place
        </span>
      </div>

      {/* Summary chips */}
      <div className="mb-4 flex flex-wrap gap-2">
        {SUMMARY_CHIPS.map((c) => {
          const n = summary[c.key] ?? 0;
          if (n === 0) return null;
          return (
            <span
              key={c.key}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                c.danger ? 'bg-red-500/15 text-red-500' : 'bg-muted text-muted-foreground'
              }`}
            >
              <span className="font-bold">{n}</span> {c.label}
            </span>
          );
        })}
        {!hasRisk && events.length === 0 && !loading && (
          <span className="text-sm text-muted-foreground">No safety events on record.</span>
        )}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading timeline…</div>
      ) : events.length === 0 ? null : (
        <ol className="space-y-3">
          {events.map((e, i) => {
            const meta =
              e.kind === 'photo_flag'
                ? photoEventMeta(e.detail)
                : (KIND_META[e.kind] ?? {
                    label: e.kind,
                    Icon: Activity,
                    tone: 'muted' as const,
                  });
            const { Icon } = meta;
            return (
              <li key={`${e.kind}-${e.ts}-${i}`} className="flex gap-3">
                <div
                  className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${TONE_CLASS[meta.tone]}`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm font-medium">{meta.label}</span>
                    {e.title && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        {e.title}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">{formatDate(e.ts)}</span>
                  </div>
                  {e.detail && (
                    <div className="mt-0.5 break-words text-sm text-muted-foreground">
                      {e.detail.length > 200 ? `${e.detail.slice(0, 200)}…` : e.detail}
                    </div>
                  )}
                  {e.actor && (
                    <div className="text-xs text-muted-foreground">by {e.actor}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
